import { defineComponent as _defineComponent } from 'vue'
import { removeAndInsetHtmlEl, showServerError } from '@/utils';
import { getOneEmployeesOffer } from '@/api/offers';
import OfferMain from "@/components/offers/offer/OfferMain.vue";
import PageLayout from "@/components_v3/layout/PageLayout.vue";
import { OFFER_TABS, OfferSingle } from "@/services/offers/OfferSingle";
import PersonalRates from "@/views/offers/personalRate/PersonalRates.vue";
import Approvals from "@/views/offers/approval/Approvals.vue";
import OfferMetrics from "@/components/offers/offer/OfferMetrics.vue";
import OfferGoalsTab from "@/components/offers/offer/OfferGoalsTab.vue";
import OfferLimitsTab from "@/components/offers/offer/OfferLimitsTab.vue";
import OfferLinks from "@/components/offers/offer/OfferLinks.vue";
import OfferRedirects from "@/components/offers/offer/OfferRedirects.vue";
import BasePageTabs from "@/components-UI/BasePageTabs.vue";
import SvgCopy from "@/assets/icons/copy2.svg";
import SvgEdit from "@/assets/icons/edit-pen.svg";
import SvgReset from "@/assets/icons/reload.svg";
import SvgCheck from "@/assets/icons/check.svg";
import OfferChangeHistory from "@/components/offers/offer/OfferChangeHistory.vue";
import OfferTexts from "@/components/offers/offer/OfferTexts.vue";
import {
    computed,
    onBeforeMount,
    onBeforeUnmount,
    ref,
    watch, nextTick,
} from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import store from "@/store";
import OfferProducts from "@/components/offers/offer/OfferProducts.vue";
import { useOfferDetailStore } from "@/store_v3/offerCard";
import { useWindowResize } from "@/utils/useWindowSize";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";
import { useCopyText } from "@/utils/copyText";
import CreateButton from "@/components-UI/buttons/CreateButton.vue";
import { storeToRefs } from "pinia";
import { setWindowTitle } from "@/services/TablePage/reportPreset";
import PrivateRateList from "@/views/offers/private-rates/PrivateRateList.vue";
import { updateQueryString } from "@/utils/updateQueryString";
import { useColumnsStore } from "@/store_v3/ColumnsStore";
import { canEditOffers } from "@/utils/checkRole";

interface Props {
    beforeListRoute: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OfferPage',
  props: {
    beforeListRoute: null
  },
  setup(__props: any) {

const props = __props as {
    beforeListRoute: string;
}


const route = useRoute();
const router = useRouter();

const tabs = ref([
    { text: 'Основное', value: OFFER_TABS.MAIN },
    { text: 'Тексты', value: OFFER_TABS.TEXTS },
    { text: 'Ссылки', value: OFFER_TABS.LINKS },
    { text: 'Цели', value: OFFER_TABS.GOALS },
    { text: 'Лимиты', value: OFFER_TABS.LIMITS },
    { text: 'Показатели', value: OFFER_TABS.METRICS },
    { text: 'Индивидуальные цены', value: OFFER_TABS.RATES },
    { text: 'Реестр цен', value: OFFER_TABS.PRIVATE_RATES },
    { text: 'Доступы партнёров', value: OFFER_TABS.APPROVALS },
    { text: 'Редиректы', value: OFFER_TABS.REDIRECTS },
    { text: 'История изменений', value: OFFER_TABS.HISTORY },
]);

const tabComponent = {
    [OFFER_TABS.MAIN]: OfferMain,
    [OFFER_TABS.TEXTS]: OfferTexts,
    [OFFER_TABS.LINKS]: OfferLinks,
    [OFFER_TABS.GOALS]: OfferGoalsTab,
    [OFFER_TABS.LIMITS]: OfferLimitsTab,
    [OFFER_TABS.METRICS]: OfferMetrics,
    [OFFER_TABS.RATES]: PersonalRates,
    [OFFER_TABS.PRIVATE_RATES]: PrivateRateList,
    [OFFER_TABS.APPROVALS]: Approvals,
    [OFFER_TABS.REDIRECTS]: OfferRedirects,
    [OFFER_TABS.PRODUCTS]: OfferProducts,
    [OFFER_TABS.HISTORY]: OfferChangeHistory,
};

const pagesWithShiftedHeader = [
    OFFER_TABS.GOALS,
    OFFER_TABS.RATES,
    OFFER_TABS.PRIVATE_RATES,
    OFFER_TABS.APPROVALS,
];

const offer = ref<OfferSingle | null>(null);
const currentTab = ref(OFFER_TABS.MAIN);
const { isUpdateOffer, targetTab, isEditModeGoals, isCreateModeGoals } = storeToRefs(useOfferDetailStore());
const { selected } = storeToRefs(useColumnsStore());
const { updateAllSelectedColumns } = useColumnsStore();
const {
    setIsUpdateOffer,
    setTargetTab,
    setIsEditModeGoals,
    setIsCreateModeGoals,
    setIsUpdateGoals,
} = useOfferDetailStore();
const { width } = useWindowResize();
const maxTitleWidth = {
    [OFFER_TABS.MAIN]: 1920,
    [OFFER_TABS.GOALS]: 1920,
    [OFFER_TABS.APPROVALS]: 2500,
    [OFFER_TABS.RATES]: 2500,
    [OFFER_TABS.PRIVATE_RATES]: 1920,
};

const getMaxTitleWidth = computed(() => maxTitleWidth[currentTab.value]);

const canEditOffer = computed(() => canEditOffers());

const getClasses = computed(() => {
    const mediumTabs = [OFFER_TABS.RATES, OFFER_TABS.APPROVALS];
    const withExport = [OFFER_TABS.RATES, OFFER_TABS.APPROVALS];
    const classes = {
        'medium-width': mediumTabs.includes(currentTab.value),
        'large-width': currentTab.value === OFFER_TABS.TEXTS,
        '--main': currentTab.value === OFFER_TABS.MAIN,
        '--get-excel': withExport.includes(currentTab.value),
        '--rates': currentTab.value === OFFER_TABS.RATES,
        '--private-rates': currentTab.value === OFFER_TABS.PRIVATE_RATES,
        '--approvals': currentTab.value === OFFER_TABS.APPROVALS,
        '--goals': currentTab.value === OFFER_TABS.GOALS,
        '--texts-tab': currentTab.value === OFFER_TABS.TEXTS,
    };

    return Object.keys(classes).filter((key) => classes[key]).join(' ');
});

const legacyOfferId = computed(() => route.params.id);

const title = computed(() => {
    return `${offer.value?.legacy_id || ''} ${offer.value?.name || ''}`;
});

const getOneOffer = async () => {
    try {
        const data = await getOneEmployeesOffer(legacyOfferId.value);
        const newOffer = new OfferSingle(data);
        await newOffer.downloadOfferData();
        store.commit('offersModule/SET_CURRENT_OFFER', { legacy_id: newOffer.legacy_id, name: newOffer.name });
        offer.value = newOffer;
        toggleProductTab();
    } catch (err) {
        showServerError(err, 'Оффер не найден');
    }
};

const toggleProductTab = () => {
    const isShowProductTab = Object.keys(offer.value?.offerParamsForms).length > 0;
    const productTab = { text: 'Параметры продукта', value: OFFER_TABS.PRODUCTS };
    const productTabIndex = tabs.value.findIndex(i => i.value === OFFER_TABS.PRODUCTS);
    if (isShowProductTab) {
        productTabIndex === -1 && tabs.value.splice(8, 0, productTab);
    } else if (productTabIndex !== -1) {
        tabs.value.splice(8, 1);
    }
};

const changeTab = (value: string) => {
    const isSameTab = currentTab.value === value;
    const isShiftedHeaderPage = pagesWithShiftedHeader.includes(value);
    updateQueryString({});
    currentTab.value = value;
    setTabToUrl();
    !isSameTab && removeHeader();
    isShiftedHeaderPage && insertHeader();
};

const insertHeader = () => {
    nextTick(() => {
        removeAndInsetHtmlEl({
            type: 'move',
            moveEl: '.base-list-layout__title-wrap',
            containerEl: '.page-layout__right',
        });
    });
};

const removeHeader = () => {
    removeAndInsetHtmlEl({
        type: 'remove',
        moveEl: '.base-list-layout__title-wrap',
        containerEl: '.page-layout__right',
    });
};

const setTabToUrl = () => {
    if (route.query.tab! === currentTab.value) return;
    const params = { ...{ tab: currentTab.value } };
    const tabsWithFilters = [OFFER_TABS.RATES, OFFER_TABS.APPROVALS, OFFER_TABS.PRIVATE_RATES];
    if (tabsWithFilters.includes(currentTab.value)) {
        const data = { ...store.getters['GET_FILTERS_PARAMS_OBJECT'], ...{ offer_legacy_id: [offer.value?.legacy_id] } };
        store.commit('SET_FILTERS_PARAMS_OBJECT', data);
        Object.assign(params, data);
    }
    // columns хранит выбранные колонки реестра цен, для остальных вкладок они чужие
    if (currentTab.value === OFFER_TABS.PRIVATE_RATES) {
        Object.assign(params, { columns: selected.value });
    } else {
        updateAllSelectedColumns([]);
    }
    updateQueryString({ ...params });
};

const backToList = () => {
    if (currentTab.value === 'tab-texts' && route.query['is_create_goals']) {
        router.replace({ query: { 'tab': 'tab-texts' } });
    } else if (router.currentRoute.query?.tab === 'tab-goals' && router.currentRoute.query?.is_create_goals) {
        setIsCreateModeGoals(false);
        router.push({
            query: {
                'tab': 'tab-goals',
            },
        });
    } else {
        router.push({ path: props.beforeListRoute });
    }
};

const copyOffer = () => {
    router.push({ name: 'create-offer', query: { copy: `${legacyOfferId.value}` } });
};

const copyTitleOnMobile = () => {
    (width.value <= 1264) && copyTitle();
};

const copyTitle = () => {
    useCopyText(title.value, 'Название и ID оффера скопировано', 'info');
};

// -- Goals
const goToGoals = () => {
    setIsEditModeGoals(true);
    changeTab('tab-goals');
};

const editGoals = () => {
    if (isEditModeGoals.value) {
        setIsUpdateGoals(true);
    } else {
        setIsEditModeGoals(true);
    }
};

const cancelEditGoals = () => {
    setIsEditModeGoals(false);
};

const createGoal = () => {
    setIsCreateModeGoals(true);
};

watch(() => targetTab, () => {
    if (!targetTab) return;
    changeTab(targetTab);
    setTargetTab('');
});

watch(() => route.query, () => {
    setTabToUrl();
    setWindowTitle(title.value);
});

watch(() => isUpdateOffer.value, () => {
    if (!isUpdateOffer.value) return;
    setIsUpdateOffer(false);
    getOneOffer();
});

onBeforeMount(async () => {
    store.commit('SET_DEFAULT_FILTERS_PARAMS_OBJECT', {});
    store.commit('SET_FILTERS_PARAMS_OBJECT', {});
    await getOneOffer();
    currentTab.value = route.query.tab! || 'tab-main';
    changeTab(currentTab.value);
});

onBeforeUnmount(() => {
    store.commit('offersModule/SET_CURRENT_OFFER', null);
});

return { __sfc: true,props, route, router, tabs, tabComponent, pagesWithShiftedHeader, offer, currentTab, isUpdateOffer, targetTab, isEditModeGoals, isCreateModeGoals, selected, updateAllSelectedColumns, setIsUpdateOffer, setTargetTab, setIsEditModeGoals, setIsCreateModeGoals, setIsUpdateGoals, width, maxTitleWidth, getMaxTitleWidth, canEditOffer, getClasses, legacyOfferId, title, getOneOffer, toggleProductTab, changeTab, insertHeader, removeHeader, setTabToUrl, backToList, copyOffer, copyTitleOnMobile, copyTitle, goToGoals, editGoals, cancelEditGoals, createGoal, PageLayout, OFFER_TABS, BasePageTabs, SvgCopy, SvgEdit, SvgReset, SvgCheck, BaseButtonV2, CreateButton }
}

})